import React, { useState } from 'react';
import axios from 'axios';
import { UserPlus, Loader2, ArrowRight, Minus, Plus, CreditCard } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEAT_PRICE = 19;

/**
 * AddSeatsCard
 * ------------
 * Renders a self-service add-on widget for purchasing extra Pro seats above
 * the included 5. Quantity stepper + Stripe Checkout redirect.
 *
 * Props:
 *   - currentSeatLimit: number — what the org currently has (display only)
 *   - currentSeatCount: number — how many members are currently using seats
 *
 * The card hides itself for non-Pro orgs (free / trial) — those need to
 * upgrade first before paying for extra seats.
 */
const AddSeatsCard = ({ currentSeatLimit, currentSeatCount, plan }) => {
  const [quantity, setQuantity] = useState(5);
  const [loading, setLoading] = useState(false);

  if (plan !== 'pro' && plan !== 'business') {
    // Hide for free / trial — they need to subscribe first.
    return (
      <Card className="bg-slate-900 border-white/10" data-testid="seats-locked-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <UserPlus className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-white mb-1">Need more seats?</div>
              <div className="text-sm text-slate-400 mb-3">
                Upgrade to Pro to add team members. Each extra seat is ${SEAT_PRICE}/month
                and unlocks dashboards + alerts for that user.
              </div>
              <Button
                onClick={() => window.location.href = '/pricing'}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="seats-upgrade-btn"
              >
                Upgrade to Pro
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const purchase = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const origin = window.location.origin;
      const r = await axios.post(
        `${API}/billing/checkout/seats`,
        {
          quantity,
          success_url: `${origin}/organization?seats_added=${quantity}`,
          cancel_url: `${origin}/organization?seats_cancelled=1`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setLoading(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-white/10" data-testid="add-seats-card">
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            <UserPlus className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-white mb-1">Add team members</div>
            <div className="text-sm text-slate-400">
              Currently using <strong className="text-white">{currentSeatCount}</strong> of{' '}
              <strong className="text-white">{currentSeatLimit ?? '—'}</strong> seats.
              Each extra seat is ${SEAT_PRICE}/month.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <Button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            size="sm"
            variant="outline"
            className="border-white/10 h-9 w-9 p-0"
            data-testid="seats-decrement-btn"
          >
            <Minus className="w-4 h-4" />
          </Button>
          <div
            className="flex-1 text-center bg-white/5 border border-white/10 rounded-lg py-2 font-bold text-white"
            data-testid="seats-quantity-display"
          >
            {quantity} seat{quantity === 1 ? '' : 's'}
          </div>
          <Button
            onClick={() => setQuantity(Math.min(100, quantity + 1))}
            size="sm"
            variant="outline"
            className="border-white/10 h-9 w-9 p-0"
            data-testid="seats-increment-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center justify-between mb-4 px-2">
          <span className="text-sm text-slate-400">Total monthly cost</span>
          <span className="text-2xl font-bold text-white">
            ${(quantity * SEAT_PRICE).toLocaleString()}
            <span className="text-sm font-normal text-slate-400">/mo</span>
          </span>
        </div>

        <Button
          onClick={purchase}
          disabled={loading}
          className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="seats-purchase-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" />Redirecting to Stripe…</>
          ) : (
            <><CreditCard className="w-4 h-4 mr-2" />Buy {quantity} seat{quantity === 1 ? '' : 's'}</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AddSeatsCard;
